import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { EchoflicksSessionJourney } from "../types";
import { buildFinalVideoDeliveryModel } from "./finalVideoDelivery";

function label(status: string) {
  return (
    {
      delivery_pending: "Render ready; delivery pending",
      queued: "Queued for delivery",
      claimed: "Channel delivery claimed",
      submitting: "Submitting to channel",
      submitted: "Submitted to channel",
      retryable_failed: "Preparation failed; retry allowed",
      delivered: "Channel accepted",
      failed: "Delivery failed",
      outcome_unknown: "Acceptance unknown; reconciliation required",
      not_started: "Delivery not started",
    }[status] ?? status.replaceAll("_", " ")
  );
}

export function FinalVideoDeliverySummary({
  journey,
}: {
  journey?: EchoflicksSessionJourney;
}) {
  const model = buildFinalVideoDeliveryModel(journey);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Final Echo delivery</CardTitle>
          <Badge
            variant={
              model.status === "failed" || model.status === "outcome_unknown"
                ? "destructive"
                : model.status === "delivered"
                  ? "secondary"
                  : "outline"
            }
          >
            {label(model.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Channel</dt>
            <dd>{model.channel ?? "not selected"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Method</dt>
            <dd>{model.method ?? "pending"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Lifecycle rows</dt>
            <dd>{model.deliveries.length}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Duplicates suppressed</dt>
            <dd>{model.duplicateSuppressedCount}</dd>
          </div>
        </dl>
        {model.reconciliationRequired ? (
          <p className="text-destructive" role="alert">
            Do not resend automatically. Confirm the provider outcome and use
            the audited reconciliation path.
          </p>
        ) : null}
        {model.reconciliations.length ? (
          <div className="space-y-1">
            <h4 className="font-medium">Reconciliation audit</h4>
            <ul
              className="space-y-1"
              aria-label="Delivery reconciliation audit"
            >
              {model.reconciliations.map((reconciliation) => (
                <li
                  className="text-muted-foreground"
                  key={
                    reconciliation.event_id ??
                    `${reconciliation.occurred_at}:${reconciliation.actor}`
                  }
                >
                  {reconciliation.resolved_status.replaceAll("_", " ")} by{" "}
                  {reconciliation.actor}: {reconciliation.reason} (
                  {reconciliation.occurred_at})
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
